/**
 * "Needs Recategorize" card shown at the top of PhotosPanel when the
 * project has photos uploaded under requirement_keys that aren't in
 * the currently-applied template (stranded after a template swap).
 *
 * UI scope:
 *   • One row per orphan bucket (same legacy category + requirement_key)
 *   • Each row → "Move N photos to …" button → opens the shared
 *     PhotoDestinationDialog with the bucket's photo_ids pre-loaded.
 */
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ArrowRight } from "lucide-react";
import PhotoDestinationDialog from "@/components/photos/PhotoDestinationDialog";

export default function OrphanPhotosCard({ project, progress, onChanged }) {
  const orphans = progress?.orphans;
  const total = orphans?.total || 0;
  const buckets = orphans?.buckets || [];
  const [target, setTarget] = useState(null); // bucket currently being reassigned

  if (!total) return null;

  return (
    <>
      <Card
        className="bg-amber-950/30 border-amber-800/60"
        data-testid="orphan-photos-card"
      >
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-display font-bold text-amber-100">
                {total} photo{total === 1 ? "" : "s"} need recategorization
              </div>
              <p className="text-xs text-amber-200/80 mt-1 leading-snug">
                These were uploaded under a previous photo template and
                don&rsquo;t match any requirement in the template applied
                to this project today. Move them into one of the
                current template&rsquo;s slots so they show up in the
                Photos grid and the closeout PDF.
              </p>
              <div className="mt-3 space-y-1.5">
                {buckets.map((b) => (
                  <div
                    key={`${b.category}/${b.requirement_key}`}
                    className="flex flex-wrap items-center gap-2 p-2 rounded border border-amber-900/50 bg-slate-950/40"
                    data-testid={`orphan-bucket-${b.requirement_key}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-amber-100">{b.label}</div>
                      <div className="text-[11px] text-amber-200/70 font-mono">
                        {b.photo_ids.length} photo{b.photo_ids.length === 1 ? "" : "s"}
                        {" · was: "}
                        <span className="text-amber-300">{b.category} / {b.requirement_key}</span>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => setTarget(b)}
                      className="bg-amber-700 hover:bg-amber-600 font-display tracking-wider"
                      data-testid={`orphan-move-${b.requirement_key}`}
                    >
                      Move <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <PhotoDestinationDialog
        projectId={project.id}
        photoIds={target?.photo_ids || []}
        progress={progress}
        title="Move {n} photos"
        open={!!target}
        onOpenChange={(v) => !v && setTarget(null)}
        onMoved={() => { setTarget(null); onChanged?.(); }}
      />
    </>
  );
}
